<template>
  <div class="itineraries-table">
    <BaseDataTable
      :columns="columns"
      :items="items"
      :row-key="rowKey"
      :loading="loading"
      :pagination="pagination"
      :scroll="scroll"
      selectable
      :selected-row-keys="selectedRowKeys"
      @change="onTableChange"
      @selection-change="$emit('selection-change', $event)"
    >
      <template slot="paradas" slot-scope="paradas">
        <ItinerariesStopsTags :tags="paradas" />
      </template>

      <template slot="usuario" slot-scope="text, record">
        <div class="itineraries-table__user">
          <span class="itineraries-table__user-name">{{ record.usuario }}</span>
          <span class="itineraries-table__user-role">{{ record.role }}</span>
        </div>
      </template>

      <template slot="data" slot-scope="text, record">
        <div class="itineraries-table__date">
          <span class="itineraries-table__date-value">{{ record.data }}</span>
          <span class="itineraries-table__date-time">{{ record.hora }}</span>
        </div>
      </template>

      <template slot="status" slot-scope="status">
        <div
          class="itineraries-table__status"
          :class="`itineraries-table__status--${status.toLowerCase()}`"
        >
          <a-icon
            :type="status === 'Ativo' ? 'check-circle' : 'minus-circle'"
            theme="filled"
          />
          {{ status }}
        </div>
      </template>

      <template slot="acoes" slot-scope="text, record">
        <div class="itineraries-table__actions">
          <a
            class="itineraries-table__link"
            href="#"
            @click.prevent="handleViewDetails(record)"
          >
            Detalhes
            <svg
              class="itineraries-table__link-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>

          <a-popover
            v-if="canChangeStatus"
            trigger="click"
            placement="bottomRight"
            overlay-class-name="itineraries-table__status-popover"
          >
            <template slot="content">
              <div class="itineraries-table__status-option">
                <span>{{ record.status }}</span>
                <a-switch
                  size="small"
                  :checked="record.status === 'Ativo'"
                  :loading="isStatusUpdating(record)"
                  :disabled="isStatusUpdating(record)"
                  @change="handleToggleStatus(record)"
                />
              </div>
            </template>

            <a-button class="itineraries-table__more-btn" shape="circle" size="small">
              <a-icon type="more" />
            </a-button>
          </a-popover>
        </div>
      </template>
    </BaseDataTable>
  </div>
</template>

<script>
import BaseDataTable from '@/shared/components/BaseDataTable.vue';
import ItinerariesStopsTags from './ItinerariesStopsTags.vue';
import { ITINERARY_STATUS } from '../../constants/itineraryStatus';

export default {
  name: "ItinerariesTable",
  components: { BaseDataTable, ItinerariesStopsTags  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: "id",
    },
    selectedRowKeys: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canChangeStatus: {
      type: Boolean,
      default: true,
    },
    statusUpdatingIds: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => ({ current: 1, perPage: 10, total: 0 }),
    },
  },
  data() {
    return {
      scroll: { x: 1318 },
      columns: [
        { 
          title: "Código", 
          dataIndex: "id", 
          key: "id", 
          width: 108 
        },
        { 
          title: "Nome", 
          dataIndex: "nome", 
          key: "nome", 
          width: 232 
        },
        {
          title: "Paradas",
          dataIndex: "paradas",
          key: "paradas",
          width: 278,
          scopedSlots: { customRender: "paradas" },
        },
        {
          title: "Usuário",
          key: "usuario",
          width: 202,
          scopedSlots: { customRender: "usuario" },
        },
        {
          title: "Data",
          key: "data",
          width: 166,
          sorter: true,
          scopedSlots: { customRender: "data" },
        },
        {
          title: "Status",
          dataIndex: "status",
          width: 119,
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: "Ações",
          key: "acoes",
          width: 165,
          scopedSlots: { customRender: "acoes" },
        },
      ],
    };
  },
  methods: {
    onTableChange({ page, perPage, sorter }) {
      this.$emit("table-change", {
        page,
        perPage,
        sort: this.buildSort(sorter),
      });
    },
    buildSort(sorter) {
      if (!sorter) return null;
      const field = sorter.field === "data" ? "updated_at" : sorter.field;
      return `${field}|${sorter.direction}`;
    },
    isStatusUpdating(record) {
      return this.statusUpdatingIds.includes(record.itineraryId);
    },
    handleViewDetails(record) {
      this.$emit("view-details", record);
    },
    handleToggleStatus(record) {
      const newStatus = record.status === 'Ativo'
        ? ITINERARY_STATUS.INATIVO
        : ITINERARY_STATUS.ATIVO;

      this.$emit("toggle-status", { record, status: newStatus });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.itineraries-table {
  &__user,
  &__date {
    display: flex;
    flex-direction: column;
  }

  &__user-name,
  &__date-value {
    color: @color-text-dark;
    font-weight: 600;
  }

  &__date-value {
    font-weight: 400;
  }

  &__user-role,
  &__date-time {
    font-size: 12px;
    color: @color-text-medium;
    margin-top: 2px;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;

    &--ativo {
      color: @color-success;
    }

    &--inativo {
      color: @color-text-muted;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: @color-primary;
    font-weight: 500;

    &:hover,
    &:focus {
      color: @color-primary-hover;
      text-decoration: none;
    }
  }

  &__link-icon {
    width: 16px;
    height: 16px;
  }

  &__more-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      color: @color-primary-hover;
      border-color: @color-primary-hover;
      background: transparent;
    }
  }
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.itineraries-table__status-popover {
  .ant-popover-inner-content {
    padding: 12px 16px;
  }
}

.itineraries-table__status-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 6px 0;
  font-size: 14px;
  color: @color-text-dark;

  .ant-switch-checked {
    background-color: @color-primary;
  }
}
</style>
